import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Box, Typography } from "@mui/material";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";

// Shared components
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";

// Types & Models
import { OptionType } from "../../../../shared/components/form-builder/form-config.model";
import { ATProductInfoDetails } from "../../atc-models/atc-org";
import { initATProductInfoDetails } from "../../atc-models/atc-initial-values";

// Hooks & Stores
import useNotification from "../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { useParams } from "react-router-dom";

// Utils
import { Utills } from "../../../../shared/app-configs/utilit";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";


// ---------------------------------------------
// Validation Schema
// ---------------------------------------------
const validationSchema = Yup.object().shape({
  productNumber: Yup.string().required("Product Number is required"),
  productComponent: Yup.string().required("Product Component is required"),
  productDetail: Yup.string().required("Product Detail is required"),
  netShippingWeight: Yup.number()
  .typeError("Please enter a valid number")
  .min(0, "Must be greater than or equal to 0")
  .required("Net Shipping Weight is required"),
supplementaryWeight: Yup.number()
  .typeError("Please enter a valid number")
  .min(0, "Must be greater than or equal to 0")
  .required("Supplementary Weight is required")
  .test(
    "not-more-than-net",
    "Supplementary cannot exceed Net Shipping Weight",
    function (value) {
      const { netShippingWeight } = this.parent;
      return (Number(netShippingWeight) || 0) >= (Number(value) || 0);
    }
  ),
});

interface ProductInformationTabProps {
  onNextTab?: () => Promise<void>;
  scStatus?: boolean;
  formRef?: React.Ref<FormikProps<ATProductInfoDetails>>;
  detailsData?: ATProductInfoDetails[];
  setDetailsData?: React.Dispatch<React.SetStateAction<ATProductInfoDetails[]>>;
}

// ---------------------------------------------
// Main Component
// ---------------------------------------------
const ProductInformationForm = forwardRef<
  FormikProps<ATProductInfoDetails> & { hasUnsavedRow: boolean },
  ProductInformationTabProps
>(({ scStatus, detailsData, setDetailsData }, ref) => {
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === "submited-sc";
  const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;

  // -------------------------------
  // Local State
  // -------------------------------
  const [data, setData] = useState<ATProductInfoDetails[]>(detailsData || []);
  const [tableData, setTableData] = useState<ATProductInfoDetails[]>(
    detailsData || []
  );
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Dropdown options
  const [productComponentOptions, setProductComponentOptions] = useState<
    OptionType[]
  >([]);
  const [productDetailOptions, setProductDetailOptions] = useState<
    OptionType[]
  >([]);

  const { notify } = useNotification();
  const { setRestrictPopUpSave } = useTransactionCertificateStore();

  // -------------------------------
  // Formik Setup
  // -------------------------------
  const formik = useFormik<ATProductInfoDetails>({
    initialValues: initATProductInfoDetails,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  // Expose formik + unsaved editing state to parent
  useImperativeHandle(
    ref,
    () => Object.assign(formik, { hasUnsavedRow: editingRowIndex !== -1 }),
    [formik, editingRowIndex]
  );

  // Keep parent state in sync (only commit complete rows)
  useEffect(() => {
    if (setDetailsData) {
      const isComplete = (row: ATProductInfoDetails) =>
        Boolean(row.productNumber) &&
        Boolean(row.productCategory) &&
        Boolean(row.productDetail);
      setDetailsData(data.filter(isComplete));
    }
  }, [data]);  

  // -------------------------------
  // Handlers
  // -------------------------------
  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: ATProductInfoDetails = { ...initATProductInfoDetails };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    setRestrictPopUpSave(true);
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
    setRestrictPopUpSave(true);
  };

  const handleCancelEdit = () => {
    setData(tableData);
    setEditingRowIndex(-1);
    formik.resetForm();
    setRestrictPopUpSave(false);
  };

  const handleSaveRow = () => {
    const updatedRow = formik.values;
    const trimmedProductNumber = (updatedRow.productNumber || "").trim();

    if (
      !trimmedProductNumber ||
      !updatedRow.productCategory ||
      !updatedRow.productDetail
    ) {
      notify("Please fill all required fields before saving", "error");
      return;
    }

    // Prevent duplicate product numbers across rows (excluding the row being edited)
    const hasDuplicateProductNumber = data.some(
      (row, idx) =>
        idx !== editingRowIndex &&
        String(row.productNumber || "").trim().toLowerCase() ===
          trimmedProductNumber.toLowerCase()
    );

    if (hasDuplicateProductNumber) {
      notify("Product number already exists", "error");
      return;
    }

    // ✅ Product Weight Validation
    const net = Number(updatedRow.netShippingWeight);
    const supp = Number(updatedRow.supplementaryWeight);

    if (isNaN(net) || net < 0 || isNaN(supp) || supp < 0) {
      notify("Net and Supplementary must be valid non-negative numbers", "error");
      return;
    }

    if (supp > net) {
      notify("Supplementary cannot exceed Net Shipping Weight", "error");
      return;
    }

    const newData = [...data];
    newData[editingRowIndex] = {
      ...updatedRow,
      productNumber: trimmedProductNumber,
    };

    setData(newData);
    setTableData(newData);
    setEditingRowIndex(-1);
    formik.resetForm();
    setRestrictPopUpSave(false);
  };
  
  const handleDeleteRow = (rowIndex: number) => {
    const newData = data.filter((_, index) => index !== rowIndex);
    setData(newData);
    setTableData(newData);
    setEditingRowIndex(-1);
  };

//API fetching

  useEffect(() => {
    const getProductCategoryCodes = async () => {
      try {
        setIsLoading(true);
        const result: any = await dtrackItService.getProductCategory();
        const options = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          code: t.code_1,
        }));
        setProductComponentOptions(options);
      } catch (err) {
        Utills.cl(`Error in Fetching Product Category Codes ${err}`);
        notify("Error fetching product category codes", "error");
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    const getProductDetailCodes = async () => {
      try {
        setIsLoading(true);
        const result: any = await dtrackItService.getProductDetailsCode();
        const options = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          code: t.code_1,
        }));
        setProductDetailOptions(options);
      } catch (err) {
        Utills.cl(`Error in Fetching Product Detail Codes ${err}`);
        notify("Error fetching product detail codes", "error");
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    getProductDetailCodes();
    getProductCategoryCodes();
  }, []);

  // -------------------------------
  // Columns Definition
  // -------------------------------
  const columns: EditableColumnDef<ATProductInfoDetails>[] = [
    {
      accessorKey: "productNumber",
      header: "Product Number",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Number
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          size="small"
          value={value ?? ""}
          placeholder="Enter Product Number"
          onChange={(e) => onChange(String(e.target.value).replace(/,/g, ""))}
          sx={{ mb: 2 }}
          fullWidth
        />
      ),
    },
    {
      accessorKey: "productCategory",
      header: "Product Category",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Category
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          size="small"
          value={value ?? ""}
          placeholder="Select Category"
          enableSearch={true}
          options={
            productComponentOptions.length ? productComponentOptions : []
          }
          onChange={(e) => onChange(e.target.value)}
          fullWidth
        />
      ),
    },
    {
      accessorKey: "productDetail",
      header: "Product Detail",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Detail
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          size="small"
          value={value ?? ""}
          placeholder="Select Detail"
          enableSearch={true}
          options={productDetailOptions.length ? productDetailOptions : []}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
        />
      ),
    },
    {
      accessorKey: "netShippingWeight",
      header: "Net Shipping Weight",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Net Shipping Weight (kg)
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          size="small"
          value={value ?? ""}
          placeholder="Enter Net Shipping Weight"
          onChange={(e) => {
            let raw = e.target.value.replace(/,/g, ".");
            raw = raw.replace(/[^0-9.]/g, "");
            const firstDot = raw.indexOf(".");
            if (firstDot !== -1) {
              raw =
                raw.slice(0, firstDot + 1) +
                raw.slice(firstDot + 1).replace(/\./g, "");
            }
            if (raw.includes(".")) {
              const [i, d] = raw.split(".");
              raw = i + "." + (d ?? "").slice(0, 2);
            }
            onChange(raw);
          }}
          inputMode="decimal"
          sx={{ mb: 2 }}
          fullWidth
        />
      ),
    },
    {
      accessorKey: "supplementaryWeight",
      header: "Supplementary Weight",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Supplementary Weight (kg)
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          size="small"
          value={value ?? ""}
          placeholder="Enter Supplementary Weight"
          onChange={(e) => {
            let raw = e.target.value.replace(/,/g, ".");
            raw = raw.replace(/[^0-9.]/g, "");
            const firstDot = raw.indexOf(".");
            if (firstDot !== -1) {
              raw =
                raw.slice(0, firstDot + 1) +
                raw.slice(firstDot + 1).replace(/\./g, "");
            }
            if (raw.includes(".")) {
              const [i, d] = raw.split(".");
              raw = i + "." + (d ?? "").slice(0, 2);
            }
            onChange(raw);
          }}
          inputMode="decimal"
          sx={{ mb: 2 }}
          fullWidth
        />
      ),
    },
  ];
  // -------------------------------
  // Render
  // -------------------------------
  return (
    <>
      {/* Loader can be hooked to API calls later */}
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            p: 1,
            border: "2px solid rgba(128, 128, 128, .2)",
            my: 4,
          }}
        >
          {/* Header + Add Button */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6" fontWeight="bold">
              Product Information
            </Typography>
            <StyledButton
              onClick={handleAddNewRow}
              disabled={isReadOnlyBasedOnStatus || editingRowIndex !== -1}
            >
              Add Product
            </StyledButton>
          </Box>

          {/* Editable Table */}
          <EditableTable
            data={data}
            columns={columns}
            onSaveRow={handleSaveRow}
            onCellChange={handleCellChange}
            editingRowIndex={editingRowIndex}
            onEditRow={handleEditRow}
            onCancelEdit={handleCancelEdit}
            onDeleteRow={handleDeleteRow}
            enableEditing={!isReadOnlyBasedOnStatus}
            isSaving={false}
          />
        </Box>
      )}
    </>
  );
});

export default ProductInformationForm;
